import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import DynamicTable from '../../../components/DynamicTable';
import LoadingModal from '../../../components/LoadingModal';
import { PeriodTransaction } from '../types';
import { useProductsAndOffers } from '../hooks/useProductsAndOffers';
import { calculateOfferPerformance } from '../Products/ProductsController';
import { formatCurrency } from '../FinancialDashboardController';
import OfferPerformanceSettings, { PerformanceThresholds } from './OfferPerformanceSettings';
import { loadThresholds, saveThresholds } from './performanceThresholdsUtils';
import './OffersDashboard.scss';

interface Props {
  monthlyTransactions: PeriodTransaction[];
}

const OffersDashboard: React.FC<Props> = ({ monthlyTransactions }) => {
  const { t } = useTranslation();
  const { products, offers, loading } = useProductsAndOffers();

  // State
  const [thresholds, setThresholds] = useState<PerformanceThresholds>(loadThresholds());
  const [showSettings, setShowSettings] = useState(false);
  const [showLegend, setShowLegend] = useState(false);

  const offerPerformances = useMemo(() =>
    calculateOfferPerformance(monthlyTransactions, offers, products, thresholds),
    [monthlyTransactions, offers, products, thresholds]
  );

  const handleSaveThresholds = (newThresholds: PerformanceThresholds) => {
    setThresholds(newThresholds);
    saveThresholds(newThresholds);
  };

  const columns = useMemo(
    () => [
      {
        Header: t('offersdashboard.table.offer-code'),
        accessor: 'OfferCode',
      },
      {
        Header: t('offersdashboard.table.offer-title'),
        accessor: 'OfferTitle',
      },
      {
        Header: t('offersdashboard.table.discount'),
        accessor: 'DiscountPercentage',
        Cell: ({ row }: any) => {
          const offer = row.original;
          if (offer.DiscountPercentage > 0) {
            return `${offer.DiscountPercentage}% ${t('productsdashboard.off')}`;
          } else if (offer.FixedPrice > 0) {
            return formatCurrency(offer.FixedPrice);
          }
          return 'N/A';
        },
      },
      {
        Header: t('offersdashboard.table.total-revenue'),
        accessor: 'TotalRevenue',
        Cell: ({ value }: any) => formatCurrency(value),
      },
      {
        Header: t('offersdashboard.table.products-sold'),
        accessor: 'ProductsSold',
        Cell: ({ value }: any) => value.toLocaleString(),
      },
      {
        Header: t('offersdashboard.table.avg-per-transaction'),
        accessor: 'AvgRevenuePerTransaction',
        Cell: ({ value }: any) => formatCurrency(value),
      },
      {
        Header: t('offersdashboard.table.performance'),
        accessor: 'Performance',
        Cell: ({ value }: any) => (
          <span className={`performance-badge ${value}`}>
            {t(`productsdashboard.performance-rating.${value}`)}
          </span>
        ),
      },
    ],
    [t]
  );

  // Calculate stats
  const totalOffers = offerPerformances.length;
  const totalRevenue = offerPerformances.reduce((sum, o) => sum + o.TotalRevenue, 0);
  const totalProductsSold = offerPerformances.reduce((sum, o) => sum + o.ProductsSold, 0);
  const excellentOffers = offerPerformances.filter(o => o.Performance === 'excellent').length;

  if (loading) {
    return <LoadingModal />;
  }

  return (
    <div className="offers-dashboard">
      <div className="offers-header">
        <div className="header-content">
          <div>
            <h1>{t('offersdashboard.title')}</h1>
            <p className="offers-subtitle">{t('offersdashboard.subtitle')}</p>
          </div>
          <div className="header-actions">
            <button
              className="legend-btn"
              onClick={() => setShowLegend(!showLegend)}
              title={t('offersdashboard.legend.toggle').toString()}
            >
              {t('offersdashboard.legend.title')}
            </button>
            <button
              className="settings-btn"
              onClick={() => setShowSettings(true)}
              title={t('offersdashboard.settings.title').toString()}
            >
              {t('offersdashboard.settings.button')}
            </button>
          </div>
        </div>

        {/* Performance Legend */}
        {showLegend && (
          <div className="performance-legend">
            <h3>{t('offersdashboard.legend.performance-criteria')}</h3>
            <div className="legend-grid">
              <div className="legend-item excellent">
                <span className="badge">{t('productsdashboard.performance-rating.excellent')}</span>
                <p>
                  {t('offersdashboard.legend.avg-revenue')}: <strong>€{thresholds.excellent.minAvgRevenue}+</strong>
                  <br />
                  <span className="and-text">{t('offersdashboard.settings.and-condition')}</span>
                  <br />
                  {t('offersdashboard.legend.products-sold')}: <strong>{thresholds.excellent.minProductsSold}+</strong>
                </p>
              </div>
              <div className="legend-item good">
                <span className="badge">{t('productsdashboard.performance-rating.good')}</span>
                <p>
                  {t('offersdashboard.legend.avg-revenue')}: <strong>€{thresholds.good.minAvgRevenue}+</strong>
                  <br />
                  <span className="or-text">{t('offersdashboard.settings.or-condition')}</span>
                  <br />
                  {t('offersdashboard.legend.products-sold')}: <strong>{thresholds.good.minProductsSold}+</strong>
                </p>
              </div>
              <div className="legend-item average">
                <span className="badge">{t('productsdashboard.performance-rating.average')}</span>
                <p>
                  {t('offersdashboard.legend.avg-revenue')}: <strong>€{thresholds.average.minAvgRevenue}+</strong>
                  <br />
                  <span className="or-text">{t('offersdashboard.settings.or-condition')}</span>
                  <br />
                  {t('offersdashboard.legend.products-sold')}: <strong>{thresholds.average.minProductsSold}+</strong>
                </p>
              </div>
              <div className="legend-item poor">
                <span className="badge">{t('productsdashboard.performance-rating.poor')}</span>
                <p>{t('offersdashboard.legend.below-average')}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Stats Overview */}
      <div className="offers-stats-overview">
        <div className="stat-card">
          <div className="stat-label">{t('offersdashboard.stats.total-offers')}</div>
          <div className="stat-value">{totalOffers}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">{t('offersdashboard.stats.total-revenue')}</div>
          <div className="stat-value">{formatCurrency(totalRevenue)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">{t('offersdashboard.stats.total-products-sold')}</div>
          <div className="stat-value">{totalProductsSold.toLocaleString()}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">{t('offersdashboard.stats.excellent-offers') + " (>)"}</div>
          <div className="stat-value">{excellentOffers}</div>
        </div>
      </div>

      {/* Offers Table */}
      <div className="offers-table-section">
        <h2>{t('offersdashboard.table.title')}</h2>
        <DynamicTable
          className="OffersPerformance"
          columns={columns}
          data={offerPerformances}
          loading={false}
          globalFilter={true}
          showActiveField={false}
          initialState={{
            pageSize: 20,
            sortBy: [{ id: 'TotalRevenue', desc: true }],
          }}
        />
      </div>

      {/* Settings Modal */}
      {showSettings && (
        <OfferPerformanceSettings
          thresholds={thresholds}
          onSave={handleSaveThresholds}
          onClose={() => setShowSettings(false)}
        />
      )}
    </div>
  );
};

export default OffersDashboard;
